import React from 'react';
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  Cell
} from 'recharts';
import { ChartDataPoint, ColorScheme, COLOR_PALETTES, getDataPointColor } from './chartUtils';

interface BarChartProps {
  data: ChartDataPoint[];
  colorScheme: ColorScheme;
  title?: string;
}

export const BarChart: React.FC<BarChartProps> = ({ data, colorScheme, title }) => {
  const gradientId = React.useId();

  // Empty state
  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-neutral-500 dark:text-neutral-400">
        <p className="text-sm">No data available</p>
      </div>
    );
  }

  // Cap data at 20 items, aggregate rest as "Others"
  let chartData = data;
  if (data.length > 20) {
    const sorted = [...data].sort((a, b) => b.value - a.value);
    const top15 = sorted.slice(0, 15);
    const others = sorted.slice(15);
    const othersSum = others.reduce((sum, item) => sum + item.value, 0);
    chartData = [...top15, { label: 'Others', value: othersSum }];
  }

  // Chart height configuration
  const ITEM_HEIGHT = 40;
  const CHART_PADDING = 60;
  const MAX_CHART_HEIGHT = 500;

  // Calculate dynamic height: ITEM_HEIGHT per item + CHART_PADDING
  const height = Math.min(chartData.length * ITEM_HEIGHT + CHART_PADDING, MAX_CHART_HEIGHT);

  return (
    <div>
      {title && (
        <h4 className="text-sm font-semibold text-neutral-900 dark:text-white mb-3">
          {title}
        </h4>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <RechartsBarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 50, left: 0, bottom: 5 }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={COLOR_PALETTES.brand.primary} />
              <stop offset="100%" stopColor={COLOR_PALETTES.brand.secondary} />
            </linearGradient>
          </defs>

          <XAxis
            type="number"
            stroke="#9ca3af"
            tick={{ fontSize: 11, fill: '#9ca3af' }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)}
          />

          <YAxis
            type="category"
            dataKey="label"
            stroke="#9ca3af"
            tick={{ fontSize: 12, fill: '#d1d5db' }}
            width={60}
            tickLine={false}
            axisLine={false}
          />

          <Tooltip
            contentStyle={{
              backgroundColor: '#1f2937',
              border: '1px solid #374151',
              borderRadius: '6px',
              fontSize: '12px'
            }}
            labelStyle={{ color: '#f3f4f6' }}
            itemStyle={{ color: '#d1d5db' }}
            cursor={{ fill: 'rgba(156, 163, 175, 0.1)' }}
            formatter={(value: number) => [value.toLocaleString(), 'Count']}
          />

          <Bar
            dataKey="value"
            radius={[0, 4, 4, 0]}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={
                  colorScheme === 'categorical'
                    ? getDataPointColor(index, colorScheme, entry.platform)
                    : colorScheme === 'brand'
                      ? `url(#${gradientId})`
                      : COLOR_PALETTES.brand.primary
                }
              />
            ))}
            <LabelList
              position="right"
              style={{ fill: '#e5e7eb', fontSize: 11 }}
              formatter={(value: number) => value.toLocaleString()}
            />
          </Bar>
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
};
